import { useEffect, type ReactNode } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { useCelebrating } from '../levelUp';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}

// Bottom sheet on phones (thumb-reachable), centered dialog on wider screens.
// Tap outside or press Escape to close. Waits while a level-up is being
// celebrated, so only one popup is ever on screen.
export function Sheet({ open: wantsOpen, onClose, label, children }: SheetProps) {
  const celebrating = useCelebrating();
  const open = wantsOpen && !celebrating;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <m.div
          key="sheet"
          className="sheet-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <m.div
            className="sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-grabber" aria-hidden />
            {children}
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

// Shared layout for the prompts that live in a Sheet.
export function SheetBody({ icon, title, body, children }: { icon: ReactNode; title: ReactNode; body?: ReactNode; children?: ReactNode }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2.75rem', lineHeight: 1, marginBottom: '0.75rem' }}>{icon}</div>
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#FFC93C', marginBottom: '0.4rem' }}>{title}</h2>
      {body && <div style={{ color: '#bbb', fontSize: '0.95rem', lineHeight: 1.45, marginBottom: '1.25rem' }}>{body}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>{children}</div>
    </div>
  );
}
